import { EventListItemInterface, IdNameEntryInterface } from '@elearning-platform/api-types/interfaces';
import { DatesRange } from '@elearning-platform/ui';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import { normalizeSearchValue } from '../../../utils';
import { FilterName } from '../enums';
import { EventPredicate, TrainingFilters } from '../types';

export const useFilteredData: (events: EventListItemInterface[], filters: TrainingFilters, searchValue: string) => EventListItemInterface[]
= (events: EventListItemInterface[], filters: TrainingFilters, searchValue: string): EventListItemInterface[] => useMemo(
  (): EventListItemInterface[] => {
    const query: string = normalizeSearchValue(searchValue.trim());
    const categories: string[] = Array.isArray(filters[FilterName.Category]) ? filters[FilterName.Category] : [];
    const types: string[] = Array.isArray(filters[FilterName.Type]) ? filters[FilterName.Type] : [];
    const dates: DatesRange | undefined = filters[FilterName.DatesRange] && !Array.isArray(filters[FilterName.DatesRange])
      ? filters[FilterName.DatesRange]
      : undefined;

    const matches: EventPredicate[] = [
      query
        ? (event: EventListItemInterface): boolean => normalizeSearchValue(event.course.name).includes(query)
        : null,
      categories.length
        ? (event: EventListItemInterface): boolean => categories.includes(event.course.category?.enum ?? '')
        : null,
      types.length
        ? (event: EventListItemInterface): boolean => event.tags.some(
            (tag: IdNameEntryInterface): boolean => types.includes(tag.id.toString())
          )
        : null,
      dates?.dateFrom
        ? (event: EventListItemInterface): boolean => !dayjs.unix(event.date.from).isBefore(dates.dateFrom)
        : null,
      dates?.dateTo
        ? (event: EventListItemInterface): boolean => !dayjs.unix(event.date.from).isAfter(dates.dateTo)
        : null,
    ].filter((p: EventPredicate | null): p is EventPredicate => p !== null);

    return events.filter(
      (event: EventListItemInterface): boolean => matches.every(
        (match: EventPredicate): boolean => match(event)
      )
    );
  },
  [events, filters, searchValue]
);
